

































































































































































import { Component, Mixins, Prop, Watch, Inject } from "vue-property-decorator";
import HasValidation from "@/mixins/HasValidation";
import { RefundSources } from "@/data/enums/payments";
import { IInvoice } from "@/models/invoices";
import {
  IInvoiceCreditRefundProvider,
  IInvoiceCreditRefundProviderMethods
} from "@/models/providers/invoiceCreditRefund";
import { CreditRefundOptionTypes, CreditRefundOption } from "@/models/invoices";

@Component({
  components: {
    "payments-table": () =>
      import("@/components/app/global/invoices/invoiceRefundPaymentsTable.vue"),
    "products-table": () =>
      import("@/components/app/global/invoices/invoiceCreditItemsTable.vue"),
    "relative-invoice-status": () =>
      import("@/components/app/global/invoices/relativeInvoiceStatus.vue")
  }
})
export default class InvoiceCreditRefundForm extends Mixins(HasValidation) {
  @Inject({ from: "invoiceCreditRefundProviderMethods" })
  public $icrProviderMethods!: IInvoiceCreditRefundProviderMethods;

  @Inject({ from: "invoiceCreditRefundProvider" })
  public icrProviderData!: () => IInvoiceCreditRefundProvider;

  // Props
  @Prop({ default: "" })
  public invoiceId!: IInvoice["id"];

  @Prop({ required: true })
  public creditRefundOptionsFiltered!: CreditRefundOption[];

  // Data
  public paymentAmounts: { [key: number]: number } = {};
  public productAmounts: { [key: number]: number } = {};
  public refundSources: typeof RefundSources = RefundSources;
  public refundSource: RefundSources = RefundSources.ORIGINAL_SOURCE;

  public selectedOption = this.creditRefundOptionsFiltered[0];
  public isProcessing = false;
  public issueCreditNote = true;
  public amountToBeRefunded = "";

  @Watch("icrProviderData.isLoading")
  public setOption(): void {
    this.selectedOption = this.creditRefundOptionsFiltered[0];
  }

  // Getters
  public get $icrProviderData() {
    return this.icrProviderData();
  }

  public get selectedOptionId() {
    return this.$_.get(this.selectedOption, "id", null);
  }

  public get isFullOrPartialRefund() {
    return (
      this.selectedOptionId === CreditRefundOptionTypes.REFUND_INVOICE_PAYMENTS
    );
  }

  public get isCreditingSelectItems() {
    return (
      this.selectedOptionId === CreditRefundOptionTypes.CREDIT_SELECT_ITEMS
    );
  }

  public get isCreditingWholeInvoice() {
    return (
      this.selectedOptionId === CreditRefundOptionTypes.CREDIT_ENTIRE_INVOICE
    );
  }

  public submit() {
    if (this.isCreditingSelectItems) {
      return this.$icrProviderMethods.creditProducts(
        this.prepareProductsPayload(),
        this.refundSource
      );
    }

    if (this.isCreditingWholeInvoice) {
      return this.$icrProviderMethods.creditAmounts(
        [],
        RefundSources.ORIGINAL_SOURCE,
        true
      );
    }

    if (this.issueCreditNote) {
      return this.$icrProviderMethods.creditAmounts(
        this.preparePaymentsPayload(),
        RefundSources.ORIGINAL_SOURCE,
        false
      );
    }

    return this.$icrProviderMethods.refundTransactions(
      this.preparePaymentsPayload()
    );
  }

  public async validateBeforeSubmit() {
    if (this.isSubmitDisabled) {
      return;
    }
    this.isProcessing = true;
    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }
      await this.submit();
      this.$emit("close");
      this.$emit("invoice-updated");
      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_updated", {
          object: this.$t("_.invoice")
        }) as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public prepareProductsPayload() {
    return this.$_.map(this.productAmounts, (amount, id) => ({
      invoice_product_id: id,
      amount: amount
    }));
  }

  public preparePaymentsPayload() {
    return this.$_.map(this.paymentAmounts, (amount, id) => ({
      invoice_payment_id: id,
      amount: amount
    }));
  }

  public onPaymentsChanged(paymentAmounts: { [key: number]: number }) {
    this.paymentAmounts = paymentAmounts;
  }

  public onProductsChanged(productAmounts: { [key: number]: number }) {
    this.productAmounts = productAmounts;
  }

  public get invoice() {
    return this.$icrProviderData.invoice || {};
  }

  public get paymentsCount() {
    return this.$_.get(this.invoice, "payments", []).length;
  }

  public get isFullRefund() {
    return (
      this.$_.get(this.invoice, "total_amount_converted", 0) ===
      this.$_.get(this.invoice, "partial_amount_credited_converted", 0) +
        this.$_.sum(this.$_.map(this.paymentAmounts, amount => amount))
    );
  }

  public get isSubmitDisabled() {
    if (
      this.isProcessing ||
      this.hasErrors ||
      !this.$icrProviderData.hasCreditRefundOptions
    ) {
      return true;
    }
    if (this.isFullOrPartialRefund && this.$_.isEmpty(this.paymentAmounts)) {
      return true;
    }
    if (this.isCreditingSelectItems && this.$_.isEmpty(this.productAmounts)) {
      return true;
    }
    return this.selectedOptionId === null;
  }

  public get amountToCredit() {
    return this.$_.sum(this.$_.map(this.productAmounts, amount => amount));
  }

  public get isDueRefund() {
    return (
      this.invoice.paid_amount_converted > 0 &&
      this.amountToCredit > this.invoice.unpaid_amount_converted &&
      this.amountToCredit <= this.invoice.total_amount_converted
    );
  }

  @Watch("isSubmitDisabled", { immediate: true })
  public onSubmitDisabled(value) {
    this.$emit("is-submit-disabled", value);
  }

  @Watch("isProcessing", { immediate: true })
  public onIsProcessing(value) {
    this.$emit("is-processing", value);
  }

  @Watch("productAmounts", { deep: true })
  public async formatAmountToBeRefunded() {
    if (!this.isCreditingSelectItems || !this.isDueRefund) return;
    const calculatedTotal = await this.$store.dispatch("api/calculate", {
      prices: [this.amountToCredit - this.invoice.unpaid_amount_converted],
      currency_id: this.$_.get(this.invoice, "currency.id")
    });
    this.amountToBeRefunded = calculatedTotal.total_formatted;
  }
}
